import React, { useState } from 'react';
import { AudioLines, Keyboard } from 'lucide-react';
import '../newsite/newsite.css';
import '../newsite/onboard.css';
import { SiteEffects, MinimalHeader, usePageMeta } from '../newsite/SiteChrome';
import { OnboardChat } from '../newsite/OnboardChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Onboard() {
  usePageMeta(
    'Start a Project — X Scale',
    'Tell us about your project. A short guided conversation is all we need to scope your build and draft a proposal.'
  );

  const [stage, setStage] = useState('intro'); // intro | mode | chat | done
  const [form, setForm] = useState({ firstName: '', lastName: '', company: '', email: '' });
  const [formError, setFormError] = useState('');
  const [starting, setStarting] = useState(false);
  const [session, setSession] = useState(null);
  const [chatMode, setChatMode] = useState('chat');

  const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();

  const startSession = async (e) => {
    e.preventDefault();
    const { firstName, lastName, company, email } = form;
    if (!firstName.trim() || !lastName.trim() || !company.trim()) {
      setFormError('Please fill in your first name, last name, and company.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFormError('Please enter a valid email.');
      return;
    }
    setFormError('');
    setStarting(true);
    try {
      const res = await fetch(`${API}/onboard/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${firstName.trim()} ${lastName.trim()}`,
          company: company.trim(),
          email: email.trim(),
        }),
      });
      if (!res.ok) throw new Error('session');
      const data = await res.json();
      setSession(data);
      setStage('mode');
    } catch {
      setFormError("Couldn't start the session — please try again.");
    } finally {
      setStarting(false);
    }
  };

  const chooseMode = (m) => {
    setChatMode(m);
    setStage('chat');
  };

  return (
    <div className="xscale-site onboard-page" data-testid="onboard-page">
      <SiteEffects />
      <MinimalHeader />

      <div className="onboard-shell">
        {stage === 'intro' && (
          <div className="onboard-intro reveal is-visible" data-testid="onboard-intro">
            <div className="eyebrow"><span /> NEW PROJECT INTAKE</div>
            <h1>Let&rsquo;s scope your project.</h1>
            <p className="onboard-sub">
              A short guided conversation — type or talk it through with our AI onboarding
              specialist. We&rsquo;ll turn it into a proposal and get back to you.
            </p>
            <form className="onboard-form" onSubmit={startSession} noValidate data-testid="onboard-contact-form">
              <div className="onboard-field-row">
                <div className="onboard-field">
                  <label htmlFor="ob-first">First name</label>
                  <input id="ob-first" value={form.firstName} autoComplete="given-name" placeholder="Jane"
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })} data-testid="contact-firstname-input" />
                </div>
                <div className="onboard-field">
                  <label htmlFor="ob-last">Last name</label>
                  <input id="ob-last" value={form.lastName} autoComplete="family-name" placeholder="Smith"
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })} data-testid="contact-lastname-input" />
                </div>
              </div>
              <div className="onboard-field">
                <label htmlFor="ob-company">Company</label>
                <input id="ob-company" value={form.company} autoComplete="organization" placeholder="Acme Inc."
                  onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-company-input" />
              </div>
              <div className="onboard-field">
                <label htmlFor="ob-email">Best email</label>
                <input id="ob-email" type="email" value={form.email} autoComplete="email" placeholder="jane@acme.com"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
              </div>
              {formError && <div className="onboard-error" data-testid="contact-form-error">{formError}</div>}
              <button type="submit" className="button button-primary" disabled={starting} data-testid="start-conversation-btn">
                {starting ? 'Starting…' : 'Continue'}
              </button>
            </form>
          </div>
        )}

        {stage === 'mode' && (
          <div className="onboard-intro reveal is-visible" data-testid="onboard-mode-select">
            <div className="eyebrow"><span /> CHOOSE YOUR STYLE</div>
            <h1>How would you like to talk it through, {form.firstName.trim()}?</h1>
            <p className="onboard-sub">
              Either way, our onboarding specialist will guide you question by question.
            </p>
            <div className="mode-cards">
              <button className="mode-card" onClick={() => chooseMode('voice')} data-testid="choose-voice-btn">
                <div className="mode-icon"><AudioLines size={22} /></div>
                <div className="mode-title">Voice conversation</div>
                <div className="mode-desc">Speak freely, hands-free. The specialist listens, responds aloud, and you can interrupt anytime.</div>
              </button>
              <button className="mode-card" onClick={() => chooseMode('chat')} data-testid="choose-chat-btn">
                <div className="mode-icon"><Keyboard size={22} /></div>
                <div className="mode-title">Type it out</div>
                <div className="mode-desc">A classic chat. Answer at your own pace — with a dictate button if you'd rather talk.</div>
              </button>
            </div>
          </div>
        )}

        {stage === 'chat' && session && (
          <OnboardChat
            sessionId={session.session_id}
            speakerName={fullName}
            initialMessages={session.messages}
            initialMode={chatMode}
            onSubmitted={() => setStage('done')}
          />
        )}

        {stage === 'done' && (
          <div className="onboard-done" data-testid="onboard-done">
            <div className="done-orb"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg></div>
            <h2>Brief received.</h2>
            <p>
              Thanks, {form.firstName.trim()}. Our team is reviewing {form.company.trim()}&rsquo;s project now —
              expect a proposal and next steps at {form.email.trim()} shortly.
            </p>
            <a className="button button-secondary" href="/" data-testid="back-home-btn">Back to X Scale</a>
          </div>
        )}
      </div>
    </div>
  );
}
